// From what the extension read off a tab to the company, the chat view and the panel's tabs (W8).
//
// The page names a company in one of four ways: its own host (a company website), a company name
// (LinkedIn, Sales Nav), a company website on a LinkedIn company page, or a sender's address
// (Gmail). Each is turned into a domain or a name and looked up in the book the dashboard already
// loaded, so the panel shows the same row the sidebar would. Free: no vendor, no Snowflake.

import type { BookAccount, TurnViewBody } from '../api/types.ts';
import type { PageContext } from './bridge.ts';

// The same four sections as the sidebar redesign (Phase 4, item 6), always in this order, so
// the panel never reshuffles as the page changes.
export const PANEL_TABS = ['scout', 'company', 'outreach', 'research'] as const;
export type PanelTab = typeof PANEL_TABS[number];

export interface CompanyHint {
  domain: string | null;
  name: string | null;
  // The company's LinkedIn page slug (or numeric id), when the page links it: the surest match.
  linkedin_slug?: string;
}

export interface AccountMatch {
  account: BookAccount | null;
  matched_by: 'linkedin' | 'domain' | 'parent_domain' | 'name' | 'domain_name' | null;
}

// Mailbox hosts that say nothing about a company.
const CONSUMER_MAIL_HOSTS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'yahoo.com', 'icloud.com',
  'me.com', 'aol.com', 'proton.me', 'protonmail.com', 'gmx.com', 'fastmail.com',
]);

// Hosts that are a platform, not the company the page is about.
const PLATFORM_HOSTS = new Set(['linkedin.com', 'mail.google.com', 'google.com', 'x.com', 'twitter.com']);

// Words a company name carries that the book's name may not.
const NAME_SUFFIXES = /\b(inc|inc\.|llc|ltd|ltd\.|limited|corp|corp\.|corporation|co|co\.|gmbh|sa|ag|plc|ai|labs|hq)\b/g;

/** Lowercase host with www stripped, from a host or a URL; "" for nothing usable. */
export function hostOf(value: string | null | undefined): string {
  const raw = (value || '').trim().toLowerCase();
  if (!raw) return '';
  let host = raw;
  try {
    host = new URL(raw.includes('://') ? raw : `https://${raw}`).hostname;
  } catch {
    return '';
  }
  if (host.startsWith('www.')) host = host.slice(4);
  return host.includes('.') ? host : '';
}

export function normaliseCompanyName(name: string | null | undefined): string {
  return (name || '')
    .toLowerCase()
    .replace(NAME_SUFFIXES, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** The slug of a LinkedIn company page address, lowercased: "linkedin.com/company/CallRail/about" → "callrail". */
export function linkedinCompanySlug(value: string | null | undefined): string {
  const found = (value || '').match(/linkedin\.com\/company\/([^/?#]+)/i);
  if (!found || !found[1]) return '';
  try {
    return decodeURIComponent(found[1]).toLowerCase();
  } catch {
    return found[1].toLowerCase();
  }
}

// The hint with a LinkedIn slug, only when there is one (so a hint without one stays two fields).
function withSlug(hint: CompanyHint, pageUrl: string | null | undefined): CompanyHint {
  const slug = linkedinCompanySlug(pageUrl);
  return slug ? { ...hint, linkedin_slug: slug } : hint;
}

/** The company a page points at: a domain where it has one, else a name, plus its LinkedIn page. */
export function companyHint(context: PageContext | null, typed: string): CompanyHint {
  // Whatever the person typed wins: it is the fallback for a page we could not read.
  const typedText = typed.trim();
  if (typedText) {
    const typedHost = hostOf(typedText);
    if (typedHost && !typedText.includes(' ')) return { domain: typedHost, name: null };
    return { domain: null, name: typedText };
  }
  if (!context) return { domain: null, name: null };

  // A tool's own site names no company.
  if (isToolSite(context)) return { domain: null, name: null };

  // A company website names itself by its host.
  if (context.kind === 'website') {
    const host = hostOf(context.host || context.url);
    return { domain: host && !PLATFORM_HOSTS.has(host) ? host : null, name: context.site_name ?? null };
  }

  // A LinkedIn or Sales Nav company page names the company, sometimes with its website.
  if (context.kind === 'linkedin_company' || context.kind === 'sales_nav_company') {
    const website = hostOf(context.company_website);
    const hint = { domain: website && !PLATFORM_HOSTS.has(website) ? website : null, name: context.company_name ?? null };
    return context.kind === 'linkedin_company' ? withSlug(hint, context.company_page_url || context.url) : hint;
  }

  // A person's page names their current company, and often links its LinkedIn page.
  if (context.kind === 'linkedin_profile' || context.kind === 'sales_nav_lead') {
    return withSlug({ domain: null, name: context.person_company ?? null }, context.person_company_url);
  }

  // Gmail: the sender's company, unless they wrote from a personal mailbox.
  const address = (context.email_from_address || '').toLowerCase();
  const senderHost = address.includes('@') ? hostOf(address.split('@').pop() || '') : '';
  if (senderHost && !CONSUMER_MAIL_HOSTS.has(senderHost)) return { domain: senderHost, name: null };
  return { domain: null, name: null };
}

function parentDomains(host: string): string[] {
  // docs.acme.com → acme.com; never a bare TLD.
  const labels = host.split('.');
  const parents: string[] = [];
  for (let index = 1; index < labels.length - 1; index += 1) {
    parents.push(labels.slice(index).join('.'));
  }
  return parents;
}

// A name with its spaces gone too, so "Call Rail" and "CallRail, Inc." agree (the server's
// `_name_key` in agent_api/reads.py does the same).
function nameKey(name: string | null | undefined): string {
  return normaliseCompanyName(name).replace(/ /g, '');
}

// The name a domain carries: "callrail.com" → "callrail", "acme.co.uk" → "acme".
function domainNameKey(domain: string | null | undefined): string {
  const host = hostOf(domain);
  if (!host) return '';
  const labels = host.split('.');
  const secondLevel = labels.length >= 3 && ['co', 'com', 'org', 'net', 'ac'].includes(labels[labels.length - 2]!);
  const label = secondLevel ? labels[labels.length - 3] : labels[labels.length - 2];
  return nameKey(label);
}

/**
 * The book row for a hint, surest first: the LinkedIn company page, the exact host, a parent
 * domain, the exact name (case, punctuation, spacing and Inc/LLC suffixes ignored), the name
 * the account's domain carries ("CallRail" finds `callrail.com`), then a leading name.
 *
 * The frame has the whole book (`/api/book` is unscoped), the same cache the server's name
 * lookup (`find_accounts_by_name`) reads, so this applies that lookup's rules here rather than
 * asking the server.
 */
export function resolveAccount(accounts: BookAccount[], hint: CompanyHint): AccountMatch {
  if (hint.linkedin_slug) {
    const byLinkedin = accounts.find(account => linkedinCompanySlug(account.linkedin_company_page) === hint.linkedin_slug);
    if (byLinkedin) return { account: byLinkedin, matched_by: 'linkedin' };
  }
  if (hint.domain) {
    const exact = accounts.find(account => hostOf(account.domain) === hint.domain);
    if (exact) return { account: exact, matched_by: 'domain' };
    for (const parent of parentDomains(hint.domain)) {
      const byParent = accounts.find(account => hostOf(account.domain) === parent);
      if (byParent) return { account: byParent, matched_by: 'parent_domain' };
    }
  }
  const wanted = normaliseCompanyName(hint.name);
  const wantedKey = nameKey(hint.name);
  if (wanted.length >= 2) {
    const exactName = accounts.find(account => nameKey(account.account) === wantedKey);
    if (exactName) return { account: exactName, matched_by: 'name' };
    const byDomainName = wantedKey.length >= 3
      ? accounts.find(account => domainNameKey(account.domain || account.website) === wantedKey)
      : undefined;
    if (byDomainName) return { account: byDomainName, matched_by: 'domain_name' };
    if (wanted.length >= 4) {
      const leading = accounts.find(account => normaliseCompanyName(account.account).startsWith(wanted + ' '));
      if (leading) return { account: leading, matched_by: 'name' };
    }
  }
  return { account: null, matched_by: null };
}

/**
 * The domain a person lookup may lean on: a book row matched by its LinkedIn page or its domain,
 * never by name alone. A person page only names their company, and a common name ("Atlas")
 * finds the wrong book account, so a name match sends nothing and Apollo's domain decides.
 */
export function personDomainFor(match: AccountMatch, hint: CompanyHint): string | null {
  const byEvidence = match.matched_by === 'linkedin' || match.matched_by === 'domain' || match.matched_by === 'parent_domain';
  if (match.account) return byEvidence ? match.account.domain || null : null;
  return hint.domain;
}

/** The slice of an Enrich answer that says where the person works on the book. */
export interface PersonCompanyAnswer {
  found: boolean;
  company_domain: string | null;
  account: { id: string; name: string | null; domain: string | null } | null;
}

/**
 * Why the panel's account must not offer pushes for this person, or null when it may.
 *
 * On a person page the panel's account is matched by the company's *name*, and Enrich then says
 * where they really work (Apollo's domain). When that is not a book account, or another one, the
 * name-matched account's Outreach list (ticks, sequence push) is not theirs to push from
 * (Vikram's test pass, 7 Oct: Mercor said "not on the book" while the push stayed live). Before
 * Enrich, nothing is known yet, so nothing is blocked.
 */
export function personCompanyBlock(answer: PersonCompanyAnswer | null, row: BookAccount | null): string | null {
  if (!answer || !answer.found) return null;
  const where = answer.company_domain || 'their company';
  if (!answer.account) return `${where} is not on the book. Request it first, then push.`;
  if (row && answer.account.id !== row.id) {
    const theirs = answer.account.name || answer.account.domain || where;
    return `They work at ${theirs}, not ${row.account}. Open ${theirs} to work them.`;
  }
  return null;
}

/** True on a tool or app the team works in, which the extension flagged: show the quiet state. */
export function isToolSite(context: PageContext | null): boolean {
  return Boolean(context && context.kind === 'website' && context.tool_site === true);
}

// What the panel says on a tool site, in place of a lookup and a Request button.
export const TOOL_SITE_LINE = 'Open a company site, LinkedIn profile or Gmail thread';

/** True on a LinkedIn or Sales Nav person page: Outreach leads with that person. */
export function isPersonPage(context: PageContext | null): boolean {
  return Boolean(context && (context.kind === 'linkedin_profile' || context.kind === 'sales_nav_lead'));
}

/** The tab the panel opens on: Outreach for a person or a Gmail thread, else Company. */
export function defaultTabFor(context: PageContext | null): PanelTab {
  if (!context) return 'company';
  if (isPersonPage(context) || context.kind === 'gmail_thread') return 'outreach';
  return 'company';
}

/** The TurnView a chat message carries: where the person is, every page field labelled by the server. */
export function turnViewFor(context: PageContext | null, domain: string | null): TurnViewBody {
  const view: TurnViewBody = { surface: 'extension' };
  if (domain) view.domain = domain;
  if (!context) return view;
  view.page_kind = context.kind;
  if (context.url) view.url = context.url;
  if (context.title) view.title = context.title.slice(0, 200);
  if (context.person_name) view.person_name = context.person_name;
  if (context.person_title) view.person_title = context.person_title;
  if (context.person_company) view.person_company = context.person_company;
  if (context.email_subject) view.email_subject = context.email_subject;
  const sender = [context.email_from_name, context.email_from_address ? `<${context.email_from_address}>` : null]
    .filter(Boolean).join(' ');
  if (sender) view.email_from = sender;
  return view;
}

/** Openers for the Research chat, worded for what is on the page. */
export function startersFor(context: PageContext | null, companyLabel: string | null): { title: string; prompt: string }[] {
  const company = companyLabel || 'this company';
  const starters = [
    { title: 'Research', prompt: `Tell me about ${company}: what they do, why now, and the best signal.` },
  ];
  if (context && (context.kind === 'linkedin_profile' || context.kind === 'sales_nav_lead')) {
    const person = context.person_name || 'this person';
    starters.push({ title: 'Right hero?', prompt: `Is ${person} the right person to write to at ${company}? Who else should I consider?` });
    starters.push({ title: 'Draft', prompt: `Draft a short first email to ${person} at ${company}.` });
  } else {
    starters.push({ title: 'Best hero', prompt: `Who is the best person to write to at ${company}, and why?` });
  }
  return starters;
}
